/* Product media gallery (§11.1): scroll-snap track with thumbnails / dots, variant media jumps,
   pauses video that scrolls away, loads Shopify's 3D viewer, and drives the lightbox
   (← / → keys, counter, opens at the clicked image). */
class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.track.children];
    this.nav = [...this.querySelectorAll('[data-gallery-go]')];
    this.lightbox = this.querySelector('[data-lightbox]');

    this.onClick = (event) => {
      const go = event.target.closest('[data-gallery-go]');
      const zoom = event.target.closest('[data-lightbox-open]');
      const step = event.target.closest('[data-lightbox-step]');
      if (go) return this.go(Number(go.dataset.galleryGo));
      if (step) return this.stepLightbox(Number(step.dataset.lightboxStep));
      // The zoom button is the keyboard route; a click anywhere on the image also opens it.
      const trigger = zoom || (event.target.matches('.gal__media img') && event.target.parentElement.querySelector('[data-lightbox-open]'));
      if (trigger) this.openLightbox(Number(trigger.dataset.lightboxOpen), trigger);
    };
    this.addEventListener('click', this.onClick);

    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && this.setCurrent(this.slides.indexOf(entry.target))),
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));

    const start = this.slides.findIndex((slide) => slide.dataset.mediaId === this.dataset.start);
    if (start > 0) this.go(start, false);
    else this.setCurrent(0);

    this.setupLightbox();
    this.loadModels();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.lightboxObserver) this.lightboxObserver.disconnect();
  }

  go(index, smooth = true) {
    const slide = this.slides[index];
    if (!slide) return;
    const behavior = smooth && window.Theme.motionAllowed() ? 'smooth' : 'auto';
    this.track.scrollTo({ left: slide.offsetLeft, behavior });
    this.setCurrent(index);
  }

  setCurrent(index) {
    if (index < 0) return;
    this.index = index;
    this.nav.forEach((button) => button.setAttribute('aria-current', String(Number(button.dataset.galleryGo) === index)));
    this.slides.forEach((slide, i) => {
      if (i !== index) slide.querySelectorAll('video').forEach((video) => video.pause());
    });
  }

  showMedia(id) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(id));
    if (index > -1) this.go(index);
  }

  setupLightbox() {
    if (!this.lightbox) return;
    this.lbTrack = this.lightbox.querySelector('[data-lightbox-track]');
    this.lbSlides = [...this.lbTrack.children];
    this.lbCount = this.lightbox.querySelector('[data-lightbox-count]');
    this.lbIndex = 0;
    this.lightboxObserver = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && this.setLightboxIndex(this.lbSlides.indexOf(entry.target))),
      { root: this.lbTrack, threshold: 0.6 }
    );
    this.lbSlides.forEach((slide) => this.lightboxObserver.observe(slide));
    this.lightbox.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.stepLightbox(1);
      if (event.key === 'ArrowLeft') this.stepLightbox(-1);
    });
  }

  openLightbox(index, opener) {
    if (!this.lightbox) return;
    this.lightbox.open(opener);
    requestAnimationFrame(() => this.scrollLightbox(index, false));
  }

  stepLightbox(step) {
    const count = this.lbSlides.length;
    this.scrollLightbox((this.lbIndex + step + count) % count, true);
  }

  scrollLightbox(index, smooth) {
    const slide = this.lbSlides[index];
    if (!slide) return;
    const behavior = smooth && window.Theme.motionAllowed() ? 'smooth' : 'auto';
    this.lbTrack.scrollTo({ left: slide.offsetLeft, behavior });
    this.setLightboxIndex(index);
  }

  setLightboxIndex(index) {
    if (index < 0) return;
    this.lbIndex = index;
    if (this.lbCount) this.lbCount.textContent = `${index + 1} / ${this.lbSlides.length}`;
  }

  loadModels() {
    if (!this.querySelector('model-viewer') || !window.Shopify || !window.Shopify.loadFeatures) return;
    window.Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (errors) => {
          if (errors) return;
          this.querySelectorAll('model-viewer').forEach((viewer) => {
            if (viewer.dataset.ui) return;
            viewer.dataset.ui = 'ready';
            new window.Shopify.ModelViewerUI(viewer);
          });
        },
      },
    ]);
  }
}

if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
